import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';
import { TestContext } from 'yup';

import { testRequiredSchema } from 'core/schemas';
import { getEmptyErrorMessage, numberSchemaWithTransform } from 'core/yup';
import { PricingStrategy, QuotePartNoteType, QuoteType } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { IPartValue } from 'quotes/types';
import { getCostValiditySchemaFields, getPurchaseCostLabel, TOneTimeSpaRow } from 'quotes/utils';

export const quotePartUpsertLineDetailsSchema = (
  quoteType = QuoteType.CUSTOMER,
  isAlternateVersionEnabled = false,
  savedQuotePart: TOneTimeSpaRow | null = null
) =>
  yup.object().shape({
    part: yup.object().label(t('labelPartNumber')).nullable(),
    manufacturer: yup.object().label(t('labelManufacturer')).nullable(),
    description: yup
      .string()
      .label(t('labelDescription'))
      .nullable()
      .when('part', {
        is: (part: IPartValue) => !part?.verified,
        then: yup
          .string()
          .nullable()
          .required(getEmptyErrorMessage(t('labelDescription'))),
      }),
    quantity: numberSchemaWithTransform()
      .label(t('labelQuantity'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelQuantity'))),
    uom: yup
      .string()
      .label(t('labelUom'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelUom'))),
    ...(QuoteType.CUSTOMER === quoteType
      ? {
          standardCost: numberSchemaWithTransform().label(t('labelStandardCost')).nullable(),
          purchaseCost: numberSchemaWithTransform()
            .label(getPurchaseCostLabel(isAlternateVersionEnabled))
            .nullable(),
          costSource: yup.object().shape({
            vendorQuoteNumber: yup
              .string()
              .label(t('labelVendorQuoteNumber'))
              .nullable()
              .test(
                'requiredVendorQuoteNumber',
                getEmptyErrorMessage(t('labelVendorQuoteNumber')),
                function (value) {
                  const context = this as TestContext & {
                    from: { value: Record<string, unknown> }[];
                  };

                  const parentValue = context.from?.[1]?.value;
                  if (!parentValue) {
                    return true;
                  }

                  const required = QuotePartHelper.requiredVendorQuoteNumber({
                    isAlternateVersionEnabled,
                    isCostingCustom: !!parentValue.isCostingCustom,
                    purchaseCost: parentValue.purchaseCost as number,
                  });

                  return !required || !!value;
                }
              ),
            ...getCostValiditySchemaFields(
              isAlternateVersionEnabled,
              (formValues) => !!(formValues.part as IPartValue)?.supplier?.isShipAndDebit,
              savedQuotePart
            ),
          }),
          isReplacementCostCustom: yup.boolean().nullable(),
          isCostingCustom: yup.boolean().nullable(),
          prioritizeLegacyCost: yup.boolean().nullable(),
          // `default(null)`: an object schema casts an absent value to `{}`, read as a pick.
          selectedSpa: yup.object().nullable().default(null),
          // The line's default agreement, mirrored from the pricing state for a pick save.
          defaultSpaAgreement: yup.object().nullable().default(null),
          // The live cost's vendor quote, mirrored from the pricing state for the cost validity.
          vendorQuoteCost: yup.object().nullable().default(null),
          replacementCost: numberSchemaWithTransform()
            .label(t('labelReplacementCost'))
            .nullable()
            .when(['isReplacementCostCustom', 'part', 'purchaseCost'], {
              is: (isReplacementCostCustom: boolean, part: IPartValue, purchaseCost: number) =>
                isReplacementCostCustom &&
                QuotePartHelper.requiredReplacementCost({
                  isAlternateVersionEnabled,
                  isShipAndDebitVendor: part?.supplier?.isShipAndDebit || false,
                  purchaseCost,
                }),
              then: numberSchemaWithTransform()
                .label(t('labelReplacementCost'))
                .nullable()
                .required(),
            }),
          replacementCostNumber: yup
            .string()
            .label(t('labelReplacementCostNumber'))
            .nullable()
            .when(['isReplacementCostCustom', 'part', 'purchaseCost', 'replacementCost'], {
              is: (
                isReplacementCostCustom: boolean,
                part: IPartValue,
                purchaseCost: number,
                replacementCost: number
              ) =>
                isReplacementCostCustom &&
                QuotePartHelper.requiredReplacementCostNumber({
                  isAlternateVersionEnabled,
                  isShipAndDebitVendor: part?.supplier?.isShipAndDebit || false,
                  purchaseCost,
                  replacementCost,
                }),
              then: yup.string().nullable().required(),
            }),
          shipmentType: yup
            .object({
              id: yup.number().label(t('labelShipmentType')).nullable().required(),
              name: yup.string().label(t('labelShipmentType')).nullable().required(),
            })
            .label(t('labelShipmentType'))
            .nullable()
            .required(),
          corporateLocation: yup
            .object({
              id: numberSchemaWithTransform()
                .label(t('labelShippingLocation'))
                .nullable()
                .required(),
              addressName: yup.string().label(t('labelShippingLocation')).nullable().required(),
            })
            .label(t('labelShippingLocation'))
            .nullable()
            .test(...testRequiredSchema(isAlternateVersionEnabled, t('labelShippingLocation'))),
          margin: numberSchemaWithTransform().label(t('labelMargin')).nullable(),
          priceBasedOn: yup.string().nullable(),
          pricingMargin: numberSchemaWithTransform().label(t('labelPricingMargin')).nullable(),
          price: numberSchemaWithTransform().label(t('labelPrice')).nullable(),
          pricingDate: yup
            .string()
            .nullable()
            .test('future-date', t('errorPricingDateMustBeFuture'), (value) => {
              if (!value) {
                return true;
              }

              const selectedDate = dayjs(value).startOf('day');
              const today = dayjs().startOf('day');

              return selectedDate.valueOf() > today.valueOf();
            }),
          pricingStrategy: isAlternateVersionEnabled
            ? yup
                .string()
                .label(t('labelPricingStrategy'))
                .oneOf(Object.values(PricingStrategy))
                .nullable()
                .required(getEmptyErrorMessage(t('labelPricingStrategy')))
            : yup.string().nullable(),
          replacementStandardCost: numberSchemaWithTransform().nullable(),
          notes: yup
            .array()
            .of(
              yup.object().shape({
                content: yup.string().required(),
                type: yup.string().oneOf(Object.values(QuotePartNoteType)).required(),
              })
            )
            .label(t('labelNotes'))
            .nullable(),
        }
      : {}),
  });
